import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { IGetAiAgentServiceOperationSettingsHistoryParams } from '../../../../../../../shared/apis/ai-agent/company/types';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { getDateRangeValidationError } from '../../../../../../../shared/utils/time';

import type { IAiAgentServiceOperationHistoryFilterForm } from '../types';

const createInitialFilterForm = (): IAiAgentServiceOperationHistoryFilterForm => ({
  startDate: null,
  endDate: null,
  keyword: '',
});

const parseDateParam = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateParam = (date: Date | null, isEndOfDay = false): string => {
  if (!date) {
    return '';
  }

  return (isEndOfDay ? dayjs(date).endOf('day') : dayjs(date).startOf('day')).toISOString().replace(/\.\d{3}Z$/, '');
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useServiceOperationHistoryFilters() {
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentServiceOperationHistoryFilterForm>(createInitialFilterForm);

  const startDateParam = searchParams.get('startDate') || '';
  const endDateParam = searchParams.get('endDate') || '';
  const keyword = searchParams.get('keyword') || '';

  const queryParams: IGetAiAgentServiceOperationSettingsHistoryParams = {
    companyId: parsePositiveInteger(companyId ?? null, 0),
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), 10),
    startDate: startDateParam || undefined,
    endDate: endDateParam || undefined,
    managerKeyword: keyword || undefined,
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentServiceOperationHistoryFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const applyFilters = () => {
    const dateRangeValidationError = getDateRangeValidationError(filterForm.startDate, filterForm.endDate);

    if (dateRangeValidationError) {
      void Swal.fire({
        icon: 'error',
        ...dateRangeValidationError,
        confirmButtonText: '확인',
      });

      return;
    }

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: formatDateParam(filterForm.startDate),
        endDate: formatDateParam(filterForm.endDate, true),
        keyword: filterForm.keyword.trim(),
        managerKeyword: '',
      }),
    );
  };

  const resetFilters = () => {
    const initialFilterForm = createInitialFilterForm();
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        keyword: '',
        managerKeyword: '',
      }),
    );
  };

  const handlePageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  useEffect(() => {
    const parsedStartDate = parseDateParam(startDateParam);
    const parsedEndDate = parseDateParam(endDateParam);

    setFilterForm({
      startDate: parsedStartDate,
      endDate: parsedEndDate,
      keyword,
    });

    if (searchParams.has('managerKeyword')) {
      const currentParams = Object.fromEntries(searchParams.entries());

      setSearchParams(
        cleanParams({
          ...currentParams,
          page: currentParams.page || '1',
          keyword,
          managerKeyword: '',
        }),
        { replace: true },
      );
    }
  }, [endDateParam, keyword, searchParams, setSearchParams, startDateParam]);

  return {
    filterForm,
    queryParams,
    updateFilterForm,
    applyFilters,
    resetFilters,
    handlePageChange,
  };
}
